/* eslint-disable react/no-array-index-key */
import React, { useState } from 'react';
import { formatSize } from 'pano-utils';
import { type CFMediaVideo, type CFVideo, LibType } from '@types';
import { LINK_LENGTH } from '@constants';
import { Collapse } from '@components/Collapse';
import Button from '@components/Button';
import Tip from '@components/Tip';
import Tabbar from '@components/Tabbar';
import Textarea from '@components/Textarea';
import useTextareaHandelrs from '@hooks/useTextareaHandelrs';
import { Validator } from '@components/Validator';
import arrayMove from '@utils/arrayMove';
import SortableList from '@components/SortableList';
import { LIB_SIZE_LIMIT, selectLibVideos } from '../../../mediaLib';
import { useCustomFeature } from '../../CustomFeatureContext';
import CVideoItem from './CVideoItem';
import CMoreLinkSetting from '../CMoreLinkSetting';
import { LayoutSetting } from '../../components/LayoutSetting';

const MAX = 10;
const sizeLimit = formatSize(LIB_SIZE_LIMIT[LibType.video]);

function CVideoSetting() {
  const { data, setData } = useCustomFeature<CFVideo>();
  const { videos, iframeVideo, layoutStyle = 0 } = data;
  const [tab, setTab] = useState(iframeVideo ? 1 : 0);
  const urlHandelrs = useTextareaHandelrs('trim', (val) =>
    setData((draft) => {
      draft.iframeVideo = val;
    })
  );

  const handleTabChange = (tabIndex: number) => {
    setData((draft) => {
      draft.videos = [];
      draft.iframeVideo = '';
    });
    setTab(tabIndex);
  };

  const handleSelectVideo = async () => {
    const { libItems } = await selectLibVideos({ multiple: true });
    if (!libItems) return;
    const items = libItems.slice(0, MAX - videos.length);
    setData((draft) => {
      items.forEach((item) => {
        draft.videos.push({
          mediaId: item.id,
          mediaUrl: item.url,
          mediaName: item.name,
          coverMediaUrl: '',
        });
      });
    });
  };

  const renderItem = (x: CFMediaVideo, i: number) => {
    return (
      <CVideoItem
        key={`${i}-${x.mediaId}`}
        data={x}
        index={i}
        listIndex={i}
        onCoverChange={(src, index) => {
          setData((draft) => {
            draft.videos[index].coverMediaUrl = src;
          });
        }}
        onDelete={(index) => {
          setData((draft) => {
            draft.videos.splice(index, 1);
          });
        }}
      />
    );
  };

  const renderTab0 = () => {
    return (
      <>
        <Button
          className="w-full"
          disabled={videos.length >= MAX}
          onClick={handleSelectVideo}
        >
          上传视频
        </Button>
        <div className="mt-20 mb-10 flex">
          <div>视频列表：</div>
          <div className="ml-10 self-end text-black-gray">{`${videos.length}/${MAX}`}</div>
        </div>
        <Validator
          value={videos}
          validFn={(value) => value.length > 0}
          warningMessage="请添加视频文件"
        >
          {({ value }) => {
            return value.length ? (
              <SortableList
                axis="y"
                lockAxis="y"
                distance={10}
                onSortEnd={(sort) => {
                  setData((draft) => {
                    draft.videos = arrayMove({
                      array: draft.videos,
                      fromIndex: sort.oldIndex,
                      toIndex: sort.newIndex,
                    });
                  });
                }}
              >
                <div>{value.map(renderItem)}</div>
              </SortableList>
            ) : (
              <>
                <Tip>{`最多可创建${MAX}个视频`}</Tip>
                <Tip>支持格式：MP4 </Tip>
                <Tip>{`编码：H.264 文件体积不超过${sizeLimit}`}</Tip>
              </>
            );
          }}
        </Validator>
      </>
    );
  };

  const renderTab1 = () => {
    return (
      <>
        <div className="mb-10">视频地址：</div>
        <Validator
          value={iframeVideo}
          validFn={(value) => !!value}
          warningMessage="请填写视频地址"
        >
          {({ value, warning }) => (
            <Textarea
              className="h-115"
              scheme="dark"
              warning={warning}
              placeholder="粘贴视频网站中的“通用代码”即可正常使用，如果不能正常观看，把代码中的http改为https，即可正常观看"
              maxLength={LINK_LENGTH}
              value={value}
              {...urlHandelrs}
            />
          )}
        </Validator>
      </>
    );
  };

  return (
    <>
      <Collapse title="视频设置">
        <Tabbar
          tabs={['本地上传', '第三方视频']}
          className="mb-20"
          activeIndex={tab}
          onChange={handleTabChange}
        />
        {tab === 0 && renderTab0()}
        {tab === 1 && renderTab1()}
      </Collapse>
      {tab === 0 && (
        <Collapse title="播放控制">
          <div className="mt-20 mb-10 flex items-end">排版方式</div>
          <LayoutSetting
            value={layoutStyle}
            onChange={(val) => {
              setData((draft) => {
                draft.layoutStyle = val;
              });
            }}
          />
        </Collapse>
      )}
      <CMoreLinkSetting />
    </>
  );
}

export default CVideoSetting;
